/**
 * Admin API client for UX feedback review.
 *
 * @module adminFeedbackApi
 */

import { apiGet } from '@/lib/apiUtils';

export interface LabelCountItem {
  readonly label: string;
  readonly count: number;
}

export interface AdminFeedbackSummary {
  readonly totalCount: number;
  readonly last7DaysCount: number;
  readonly last30DaysCount: number;
  readonly avgOverallRating: number | null;
  readonly avgGoalSupportRating: number | null;
  readonly contactConsentCount: number;
  readonly usageFrequencyBreakdown: readonly LabelCountItem[];
  readonly primaryAreaBreakdown: readonly LabelCountItem[];
  readonly deviceContextBreakdown: readonly LabelCountItem[];
}

export interface AdminFeedbackListItem {
  readonly id: number;
  readonly createdAt: string;
  readonly overallRating: number;
  readonly goalSupportRating: number;
  readonly usageFrequency: string;
  readonly primaryArea: string;
  readonly deviceContext: string;
  readonly contactConsent: boolean;
  readonly userId: string | null;
}

export interface AdminFeedbackListResponse {
  readonly items: readonly AdminFeedbackListItem[];
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
  readonly limit: number;
}

export interface AdminFeedbackDetail {
  readonly id: number;
  readonly userId: string | null;
  readonly username: string | null;
  readonly email: string | null;
  readonly overallRating: number;
  readonly usageFrequency: string;
  readonly goalSupportRating: number;
  readonly usedAreas: readonly string[];
  readonly primaryArea: string;
  readonly deviceContext: string;
  readonly likedMost: string;
  readonly improvementAreas: readonly string[];
  readonly experienceDetails: string;
  readonly suggestions: string;
  readonly contactConsent: boolean;
  readonly contactEmail: string | null;
  readonly createdAt: string;
}

export async function fetchAdminFeedbackSummary(): Promise<AdminFeedbackSummary | null> {
  return apiGet<AdminFeedbackSummary>('/admin/feedback/summary', {
    errorMessage: 'Failed to load feedback summary',
    context: 'fetchAdminFeedbackSummary',
  });
}

export async function fetchAdminFeedbackList(
  page: number,
  limit: number,
): Promise<AdminFeedbackListResponse | null> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  return apiGet<AdminFeedbackListResponse>(`/admin/feedback?${params.toString()}`, {
    errorMessage: 'Failed to load feedback list',
    context: 'fetchAdminFeedbackList',
  });
}

export async function fetchAdminFeedbackDetail(
  id: number,
): Promise<AdminFeedbackDetail | null> {
  return apiGet<AdminFeedbackDetail>(`/admin/feedback/${id}`, {
    errorMessage: 'Failed to load feedback detail',
    context: 'fetchAdminFeedbackDetail',
  });
}
